import { saveAs } from "file-saver";
import { toPng } from "html-to-image";

/**
 * Captures an HTML element as a PNG image and triggers a download for the user.
 *
 * @param elementId - The ID of the HTML element to capture (without the '#' prefix)
 * @param filename - The desired filename for the downloaded image (without extension)
 * @throws {Error} If the element is not found or if screenshot generation fails
 * @returns Promise<void> - Resolves when the download is triggered successfully
 *
 * @example
 * // Capture element with id="MyElement" and save as "abc.png"
 * await elementToPNG("MyElement", "abc");
 */
export const elementToPNG = async (elementId: string, filename: string): Promise<void> => {
	try {
		// Get the element by ID
		const element = document.getElementById(elementId);

		if (!element) {
			const errorMessage = `Element with id "${elementId}" not found in the DOM`;

			console.error(errorMessage);
			throw new Error(errorMessage);
		}

		// Wait for fonts to load to ensure correct rendering
		await document.fonts.ready;

		// Convert the element to PNG blob
		// Note: skipFonts set to false to embed self-hosted fonts from /src/assets/fonts
		// Note: useCORS disabled to avoid CORS issues with self-hosted fonts
		// Note: pixelRatio set to 2 for high-quality retina display screenshots
		const dataUrl = await toPng(element, {
			cacheBust: true, // Prevent caching issues
			pixelRatio: 2, // Use 2x pixel ratio for high-quality screenshots
			skipFonts: false, // Embed fonts (self-hosted, no CORS issues)
			preferredFontFormat: "truetype", // Use truetype format for fonts
			useCORS: false, // Disable CORS to avoid issues with self-hosted fonts
			allowTaint: true, // Allow tainted canvas for self-hosted resources
		});

		// Convert data URL to blob for file-saver without using fetch to avoid CSP issues
		const base64Data = dataUrl.split(",")[1];
		const byteCharacters = atob(base64Data);
		const byteNumbers = new Array(byteCharacters.length);

		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: "image/png" });

		// Ensure filename has .png extension
		const finalFilename = filename.endsWith(".png") ? filename : `${filename}.png`;

		// Trigger download
		saveAs(blob, finalFilename);
	} catch (error) {
		const errorMessage = `Failed to capture screenshot of element "${elementId}": ${
			error instanceof Error ? error.message : String(error)
		}`;

		console.error(errorMessage, error);
		throw new Error(errorMessage);
	}
};
